import React, { useEffect, useMemo } from "react";
import { UtilsUser } from "../../utils/UtilsUser";
import DigitalLegacyAuth from "../../pages/nonauth/DigitalLegacyAuth/DigitalLegacyAuth";
import { ApplicationState } from "../../store";
import { UserState } from "../../store/reducers/user-reducer";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { AUTHENTICATED_ROUTES, ROUTES } from "../../configs/routes";
import { deepLinkService } from "../../deeplink/DeepLinkService";

import { useLoginOptionHooks } from "../../hooks/login-option-hooks";

interface Props {
  children?: React.ReactNode;
}

const DeepLinkChecker: React.FC<Props> = (props) => {
  const currentURL = window.location.pathname;
  const { pathname } = useLocation();

  const { userCountryCode } = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );

  const { initiateLogin } = useLoginOptionHooks();

  const result = useMemo(
    () => deepLinkService.resolve(currentURL),
    [currentURL]
  );

  const isDLRequestDeepLink =
    result.status === "VALID" && result.deepLink.type === "SPDL";

  useEffect(() => {
    if (UtilsUser.isUserLoggedIn()) return;

    // Case 1: User is on an authenticated route directly (e.g., /my-data/SP-DEV-123)
    // Save the deep link so it survives the SA portal redirect.
    if (result.status === "VALID") {
      // Don't overwrite an existing pending deep link (e.g., from a previous save)
      const existing = deepLinkService.get();
      if (!existing) {
        deepLinkService.save(result.deepLink);
      }
    }

    // Case 2: User was redirected by the auth guard to /?redirect=/my-data
    // This happens when an unauthenticated user tries to access an auth route.
    // Only initiate login if we don't already have a pending deeplink from Case 1.
    // This preserves the full destination path (e.g., /my-data/SP-DEV-123) over
    // the sanitized redirect param (e.g., /my-data).
    const params = new URLSearchParams(window.location.search);
    const redirectParam = params.get("redirect");

    if (
      redirectParam &&
      !redirectParam.includes("success=1") &&
      (AUTHENTICATED_ROUTES.includes(redirectParam) ||
        redirectParam.includes(ROUTES.MYDATA))
    ) {
      const existingDeepLink = deepLinkService.get();
      if (!existingDeepLink) {
        initiateLogin(redirectParam);
      }
    }
  }, [pathname, userCountryCode]);

  // IF DEEPLINK is Digital Legacy and user is not yet LOGGED IN, REDIRECT to DL REDIRECT PAGE
  if (isDLRequestDeepLink && !UtilsUser.isUserLoggedIn()) {
    return <DigitalLegacyAuth />;
  }
  // IF NORMAL DEEPLINK: RETURN CHILDREN COMPONENTS
  return <>{props.children}</>;
};

export default DeepLinkChecker;
